import { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function AdminCommission() {
  const [rules, setRules] = useState([]);
  const [branches, setBranches] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ branch_id: "", therapist_id: "", service_id: "", package_id: "", booking_type: "spa", amount: 0 });

  const load = async () => setRules((await api.get("/admin/commission-rules")).data);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data));
    api.get("/therapists").then((r) => setTherapists(r.data));
    api.get("/packages").then((r) => setPackages(r.data));
    api.get("/services").then((r) => setServices(r.data));
    load();
  }, []);

  const create = async () => {
    if (!form.amount || (!form.package_id && !form.service_id)) return toast.error("Pick a service/package and amount");
    try {
      const payload = { ...form, amount: Number(form.amount) };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.post("/admin/commission-rules", payload);
      toast.success("Rule created");
      setForm({ branch_id: "", therapist_id: "", service_id: "", package_id: "", booking_type: "spa", amount: 0 });
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete rule?")) return;
    await api.delete(`/admin/commission-rules/${id}`);
    toast.success("Deleted");
    load();
  };

  const label = (r) => {
    const branch = branches.find((b) => b.id === r.branch_id)?.name || "All branches";
    const t = therapists.find((x) => x.id === r.therapist_id)?.display_name || "Default";
    const item = packages.find((p) => p.id === r.package_id)?.name || services.find((s) => s.id === r.service_id)?.name || "?";
    return { branch, t, item };
  };

  return (
    <div data-testid="admin-commission">
      <div className="label-eyebrow mb-2">Master Only</div>
      <h1 className="text-3xl font-heading text-white mb-8">Commission Engine</h1>

      <div className="border border-hgold/40 bg-hgold/5 p-6 mb-8">
        <div className="label-eyebrow mb-4 text-hgold">Add rule</div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3">
          <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 text-white outline-none text-sm">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select value={form.therapist_id} onChange={(e) => setForm({ ...form, therapist_id: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 text-white outline-none text-sm">
            <option value="">Default (all)</option>
            {therapists.map((t) => <option key={t.id} value={t.id}>{t.display_name}</option>)}
          </select>
          <select value={form.package_id} onChange={(e) => setForm({ ...form, package_id: e.target.value, service_id: "" })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 text-white outline-none text-sm">
            <option value="">— package —</option>
            {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value, package_id: "" })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 text-white outline-none text-sm">
            <option value="">— service —</option>
            {services.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={form.booking_type} onChange={(e) => setForm({ ...form, booking_type: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 text-white outline-none text-sm">
            <option value="spa">Spa</option>
            <option value="doorstep">Doorstep</option>
          </select>
          <input type="number" placeholder="Amount ₹" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-black border border-white/10 focus:border-hgold px-3 py-2 text-white outline-none text-sm" />
          <button onClick={create} data-testid="commission-add-btn" className="gold-btn justify-center" style={{ padding: "0.5rem 1rem" }}><Plus size={14}/> Add</button>
        </div>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-hpaper border-b border-white/10">
            <tr>
              {["Branch", "Therapist", "Item", "Type", "Commission", ""].map((h) => (
                <th key={h} className="text-left label-eyebrow px-4 py-3 text-white/60">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rules.map((r) => {
              const info = label(r);
              return (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-white">{info.branch}</td>
                  <td className="px-4 py-3 text-white">{info.t}</td>
                  <td className="px-4 py-3 text-white">{info.item}</td>
                  <td className="px-4 py-3 text-white/60 uppercase text-xs tracking-widest">{r.booking_type}</td>
                  <td className="px-4 py-3 text-hgold">{inr(r.amount)}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14}/></button></td>
                </tr>
              );
            })}
            {rules.length === 0 && <tr><td colSpan="6" className="text-center py-12 text-hsecondary">No rules yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
